<template>
  <section class="smart-recruiters-detail__section smart-recruiters-detail__section-seven">
    <h3 class="smart-recruiters-detail__contact-title">{{ fields.headlineContact?.value }}</h3>
    <div class="smart-recruiters-detail__contact-description-wrapper">
      <richtext-fi
        class="smart-recruiters-detail__contact-description"
        :html-content="jobAdditionalInformationFormatted"
      />
      <div v-intersect="onIntersect" />
      <div
        :class="{
          'smart-recruiters-detail__apply-now-wrapper': true,
          'smart-recruiters-detail__apply-now-wrapper--sticky': stickyApplyButton,
        }"
      >
        <grid-container-fi>
          <div class="smart-recruiters-detail__apply-now-inner">
            <button-fi
              :href="jobApplyUrl"
              :title="fields.buttonContact?.value"
              class="smart-recruiters-detail__apply-now button-fi__primary--large"
              target="_blank"
              rel="noopener noreferrer"
              natural-events
            >
              {{ fields.buttonContact?.value }}
            </button-fi>
          </div>
        </grid-container-fi>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ButtonFi, RichtextFi } from 'atoms';
import { computed, ref } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useFields } from '@/composables/fields';
import { useIsMounted } from '@/composables/isMounted';
import makeMailtoLinks from '@/utils/makeMailtoLinks';

defineOptions({ name: 'SmartRecruitersDetailSectionSeven' });

const props = withDefaults(
  defineProps<{
    jobId?: string;
    jobApplyUrl?: string;
    jobAdditionalInformation?: string;
  }>(),
  {
    jobId: '',
    jobApplyUrl: '#',
    jobAdditionalInformation: '',
  },
);

const fields = useFields('SmartRecruitersDetailPageFields');

const stickyApplyButton = ref(false);

const isMounted = useIsMounted();

/**
 * `makeMailtoLinks` needs a DOM, so it must not be reachable from a render: the server has none and
 * a throw there costs the whole rendering its markup. Until mount the text renders as it came, which
 * differs only in that the addresses are not links yet - so both sides of the first render agree,
 * and the contact text and the apply button stay in the server's markup.
 */
const linkedAddresses = (html: string) => {
  try {
    return makeMailtoLinks(html);
  } catch (error) {
    console.error('Could not link the contact addresses: ', error);
    return html;
  }
};

const jobAdditionalInformationFormatted = computed(() => ({
  value: isMounted.value
    ? linkedAddresses(props.jobAdditionalInformation)
    : props.jobAdditionalInformation,
}));

function onIntersect(
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
  isIntersecting: boolean,
) {
  const isAboveView = (entries?.[0]?.boundingClientRect?.y ?? 0) < 0;
  stickyApplyButton.value = !isIntersecting && !isAboveView;
}
</script>

<style src="./smart-recruiters-detail-section-seven" />
